export const BIOTRON_SAFE_INSPECTOR_SCHEMA_VERSION = 1
export const BIOTRON_REVIEWED_FIRMWARE_VERSION = "1.9.1"
export const BIOTRON_DIAGNOSTICS_LOCK_NAME = "playtronica-biotron-safe-inspector"

export const BIOTRON_SAFE_INSPECTOR_PROBES = Object.freeze([
  {id: "tab_lock", label: "Only one test page is open"},
  {id: "device_session", label: "The same Biotron stayed connected"},
  {id: "port_topology", label: "Both Biotron USB ports replied"},
  {id: "firmware", label: "Firmware 1.9.1 was confirmed on both ports"},
  {id: "persisted_readback", label: "Both ports returned the same saved settings"}
])

const evidenceKinds = new Set([
  "automatic_probe", "human_attestation", "environment", "blocked"
])
const evidenceStatuses = new Set(["pass", "fail", "blocked"])

const freezeEvidence = evidence => Object.freeze({
  probe: evidence.probe,
  status: evidence.status,
  kind: evidence.kind,
  reason: evidence.reason || "",
  observed_at: evidence.observed_at
})

const freezeSession = session => Object.freeze({
  ...session,
  evidence: Object.freeze(session.evidence.map(freezeEvidence))
})

export function createBiotronInspectorSession({sessionId, buildId, deviceEpoch, now}) {
  if (!sessionId) throw new Error("Safe Inspector requires a session ID.")
  return freezeSession({
    schema_version: BIOTRON_SAFE_INSPECTOR_SCHEMA_VERSION,
    session_id: sessionId,
    settings_build: buildId || "unknown",
    device_epoch: Number(deviceEpoch) || 0,
    started_at: now || new Date().toISOString(),
    state: "preflight",
    valid: true,
    invalid_reason: "",
    evidence: []
  })
}

export function appendBiotronInspectorEvidence(session, evidence) {
  if (!session || !session.valid) return session
  if (!BIOTRON_SAFE_INSPECTOR_PROBES.some(probe => probe.id === evidence.probe)) {
    throw new Error(`Unknown Safe Inspector probe: ${evidence.probe}`)
  }
  if (!evidenceKinds.has(evidence.kind) || !evidenceStatuses.has(evidence.status)) {
    throw new Error("Invalid Safe Inspector evidence.")
  }
  return freezeSession({
    ...session,
    state: "verifying",
    evidence: [...session.evidence, {
      ...evidence,
      observed_at: evidence.observed_at || new Date().toISOString()
    }]
  })
}

export function invalidateBiotronInspectorSession(session, reason, now) {
  if (!session || !session.valid) return session
  return freezeSession({
    ...session,
    state: "blocked",
    valid: false,
    invalid_reason: reason || "session_changed",
    invalidated_at: now || new Date().toISOString()
  })
}

export function biotronInspectorSummary(session) {
  if (!session || !session.valid) return "incomplete"
  const latest = new Map()
  for (const evidence of session.evidence) latest.set(evidence.probe, evidence)
  if ([...latest.values()].some(evidence => evidence.status === "fail")) return "fail"
  if (BIOTRON_SAFE_INSPECTOR_PROBES.some(probe =>
    !latest.has(probe.id) || latest.get(probe.id).status !== "pass"
  )) return "incomplete"
  return "pass"
}

export function isReadOnlyBiotronInspectorRequest(message) {
  const bytes = Array.from(message || [])
  const version = bytes.length === 6 && bytes[0] === 0xf0 && bytes[1] === 0x14 &&
    bytes[2] === 0x0d && bytes[3] === 126 && bytes[4] <= 0x7f && bytes[5] === 0xf7
  const settings = bytes.length === 7 && bytes[0] === 0xf0 && bytes[1] === 0x14 &&
    bytes[2] === 0x0d && bytes[3] === 123 && bytes[4] === 1 &&
    bytes[5] <= 0x7f && bytes[6] === 0xf7
  return version || settings
}

export function biotronInspectorFirmwareMatches(versions) {
  return Array.isArray(versions) && versions.length === 2 &&
    versions.every(version => version === BIOTRON_REVIEWED_FIRMWARE_VERSION)
}

export function createBiotronInspectorReport({session, browserEnvironment, ports}) {
  return {
    schema_version: BIOTRON_SAFE_INSPECTOR_SCHEMA_VERSION,
    created_at: new Date().toISOString(),
    product: "Biotron",
    summary: biotronInspectorSummary(session),
    session_id: session ? session.session_id : "unknown",
    settings_build: session ? session.settings_build : "unknown",
    browser_environment: browserEnvironment || "unknown",
    invalid_reason: session ? session.invalid_reason : "missing_session",
    results: BIOTRON_SAFE_INSPECTOR_PROBES.map(probe => {
      const entries = session ? session.evidence.filter(item => item.probe === probe.id) : []
      const evidence = entries.at(-1)
      return {
        id: probe.id,
        label: probe.label,
        status: evidence ? evidence.status : "pending",
        evidence_kind: evidence ? evidence.kind : "none",
        reason: evidence ? evidence.reason : ""
      }
    }),
    ports: Array.isArray(ports) ? ports.map(port => ({
      label: port.label,
      firmware: port.firmware,
      settings_clean: Boolean(port.settingsClean),
      dirty_generation: port.dirtyGeneration,
      persisted_generation: port.persistedGeneration
    })) : []
  }
}

export async function sha256Hex(value, cryptoApi) {
  if (!cryptoApi || !cryptoApi.subtle) throw new Error("SHA-256 is unavailable.")
  const bytes = new TextEncoder().encode(value)
  const digest = await cryptoApi.subtle.digest("SHA-256", bytes)
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("")
}
